import { Injectable } from '@angular/core';
import { FeatureFlag } from 'app/viewmodels/configuration/feature-flag';
import { ConfigService } from '../core/config.service';

export const FEATURE_TOGGLES = {
  /**
   * Whether or not to show the Admin > Maintenance Strategies page and related functionality
   */
  adminMaintenanceStrategy: 'admin-maintenance-strategy',
  /**
   * Build/Nodes
   */
  buildNodes: 'buildNodes',
  /**
   * Data Upload Line List Option
   */
  dataUploadLineListOption: 'data-upload-line-list-option',
  /**
   * Event Stage Action Permission
   */
  eventActionPermission: 'event-action-permission',
  /**
   * Event Stage Permission
   */
  eventStagePermission: 'event-stage-permission',
  /**
   * Mass Criticality Templates application and related functionality
   */
  massCriticalityTemplates: 'mass-criticality-templates',
  /**
   * Mitigation Availability
   */
  mitigationAvailability: 'mitigation-availability',
  /**
   * Mitigation Deferrals
   */
  mitigationDeferrals: 'mitigation-deferrals',
  /**
   * Model Clone feature
   */
  modelClone: 'model-clone',
  /**
   * permissions cached in read store tables
   */
  permissionCache: 'permission-cache',
  /**
   * RBI/ Risk Projection
   */
  riskProjection: 'rbi-projection',
  /**
   * Whether or not to show the Risk Transition table and related functionality
   */
  riskTransition: 'risk-transition',
  /*
   * RBI Summary Report on Report Type selection the Reports Page
   */
  rbiSummaryReport: 'rbi-summary-report',
  /*
   * Regionalisation/Translation feature.
   */
  regionalisation: 'regionalisation',
  /*
  * Enables risk analysis summary report
  */
  riskAnalysisSummary: 'Risk-Analysis-Summary',
  /**
   * Mass Risk run functionality on mass risk page
   */
  massRiskRun: 'risk-mass-run',
  /*
  * enable Thickness Location Move
  */
  thicknesslocationmove: 'thickness-location-move',
  /**
   * Display mitigation plan notes functionality
   */
  mitigationNotes: 'mitigation-notes',
  /**
   * Require report approvals workflow
   */
  reportApprovals: 'report-approvals',
  /**
   * Site inspection report summary
   */
  siteInspectionReportSummary: 'site-inspection-report-summary',
  /**
   * Site thickness report summary
   */
  siteThicknessReportSummary: 'site-thickness-report-summary',
};

/**
 * Service to get application feature flags
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureService {
  private features: FeatureFlag[] = [];

  constructor(configService: ConfigService) {
    const remoteConfig = configService.getRemoteConfiguration();

    if (remoteConfig && remoteConfig.featureFlags) {
      this.features = remoteConfig.featureFlags;
    }
  }

  /**
   * Gets a value indicating if the feature is enabled
   * @param feature Name of the feature
   */
  public isEnabled(feature: string): boolean {
    const wildcardFlag = this.features.find(f => f.key === '*');

    if (wildcardFlag) {
      return wildcardFlag.enabled;
    }

    const flag = this.features.find(f => f.key === feature);

    return flag ? flag.enabled : false;
  }

  /**
   * Gets a flag indicating if the Event Action Permission feature is enabled.
   */
  public eventActionPermissionEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.eventActionPermission);
  }

  /**
   * Gets a flag indicating if the Event Stage Permission feature is enabled.
   */
  public eventStagePermissionEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.eventStagePermission);
  }

  public maintenanceStrategyEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.adminMaintenanceStrategy);
  }

  /**
   * Gets a flag indicating if the Mass Criticality Templates feature is enabled
   */
  public massCriticalityTemplatesEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.massCriticalityTemplates);
  }

  /**
  * Gets a flag indicating if the Mass Risk Run functionality is available on Mass Risk pages
  */
  public massRiskRunEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.massRiskRun);
  }

  /**
   * Gets a flag indicating if the model-clone feature is enabled
   */
  public modelCloneEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.modelClone);
  }

  /**
   * Gets a flag indicating if the RBI summary report feature is enabled
   */
  public rbiSummaryReportEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.rbiSummaryReport);
  }

  public regionalisationEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.regionalisation);
  }

  /**
   * Gets a flag indicating if the Data Upload Line List Option feature is enabled
   */
  public dataUploadLineListOptionEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.dataUploadLineListOption);
  }

  /**
   * Gets a flag indicating if the build nodes feature is enabled
   */
  public buildNodesEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.buildNodes);
  }

  /**
   * Gets a flag indicating if the permission cache feature is enabled
   */
  public permissionCacheEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.permissionCache);

  /**
    * Gets a flag indicating if the feature Mitigation Availability is enabled
    */

  public mitigationAvailabilityEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.mitigationAvailability);

  /**
   * Get a flag indicating if the feature mitigation-deferrals is enabled
   */
  public mitigationDeferralsEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.mitigationDeferrals);

  /**
   * Get a flag indicating if the feature rbi-projection is enable
   */
  public riskProjectionEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.riskProjection);

  /**
   * Get a flag indicating if the feature risk-transition is enable
   */
  public riskTransitionEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.riskTransition);

  /**
  * Gets a flag indicating if the Site Asset Hub feature is enabled.
  */
  /**
  ** Gets a flag indicating to enable Thickness Location Move or not
 */
  public thicknessLocationMoveEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.thicknesslocationmove);

  /*
  * Gets a flag indicating if mitigation notes functionality is enabled
  */
  public mitigationNotesEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.mitigationNotes);

  /*
  * Gets a flag indicating to risk Analysis Summary Enabled
  */
  public riskAnalysisSummaryEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.riskAnalysisSummary);

  /*
  * Gets a flag indicating if report approvals workflow is enabled
  */
  public reportApprovalsEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.reportApprovals);

  /*
  * Gets a flag indicating if site inspection report summary is enabled
  */
  public siteInspectionReportSummaryEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.siteInspectionReportSummary);

  /*
  * Gets a flag indicating if site thickness report summary is enabled
  */
  public siteThicknessReportSummaryEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.siteThicknessReportSummary);
}
